import { FC } from 'react'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { PLATFORM_LABEL } from 'src/pages/ReviewAnalysis/constants'
import { getPlatformIcon } from 'src/pages/ReviewAnalysis/platformIcons'
import {
  DOMINANT_LABEL,
  MentionsPoint,
  PlatformShare,
  TopicSentiment,
  dominantSentiment,
  sentimentFractions,
} from 'src/interfaces/topics.interface'
import { ReviewPlatform } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './topicDetailModal.module.scss'
import { SentimentBar } from '../SentimentBar'
import { SentimentHoverCard } from '../SentimentHoverCard'

// Review platforms Social Listening has no icon for (google, playstore,
// appstore) take the Review Analysis logo and name — Google Maps, not a globe.
const reviewOnly = (platform: string): platform is ReviewPlatform =>
  platform in PLATFORM_LABEL && !(platform in SOCIAL_ICONS)

const platformIcon = (platform: string) => {
  if (reviewOnly(platform)) {
    const Icon = getPlatformIcon(platform)
    return <Icon />
  }
  return SOCIAL_ICONS[platform] ?? SOCIAL_ICONS.earth
}

// Brand spellings a plain capitalise gets wrong.
const SOCIAL_LABEL: Record<string, string> = {
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
  twitter: 'X (Twitter)',
  youtube: 'YouTube',
}

const platformName = (platform: string) => {
  if (reviewOnly(platform)) return PLATFORM_LABEL[platform]
  if (platform in SOCIAL_LABEL) return SOCIAL_LABEL[platform]
  return platform.replace(/(^|\s)\S/g, (c) => c.toUpperCase())
}

const SENTIMENT_COLORS = {
  negative: '#FF6833',
  neutral: '#C7CED1',
  positive: '#8080FF',
}

// Legend order, left to right.
const LEGEND: ('positive' | 'neutral' | 'negative')[] = [
  'positive',
  'neutral',
  'negative',
]

// Ring order, clockwise from 12 o'clock. NOT the legend order: the design puts
// negative straight after positive so the two opinionated slices sit together
// and neutral closes the circle.
const RING_ORDER: ('positive' | 'neutral' | 'negative')[] = [
  'positive',
  'negative',
  'neutral',
]

// The headline reads in the colour of the sentiment it names.
const HEADLINE_CLASS = {
  negative: styles.donutHeadlineNegative,
  neutral: styles.donutHeadlineNeutral,
  positive: '',
}

const LEGEND_LABEL = {
  negative: 'Negative',
  neutral: 'Neutral',
  positive: 'Positive',
}

// 100 x 100 ring. r and the stroke width are chosen so the ring's outer edge
// lands exactly on the box: 2 * (r + stroke/2) = 100.
const SIZE = 100
const STROKE = 12
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
// Figma draws the three arcs with a small break between them.
const ARC_GAP = 3

/**
 * "English" for the mention card's pill, from the ISO code the API sends.
 * Falls back to the upper-cased code when the browser cannot name it.
 */
export const languageName = (code?: string | null): string => {
  const raw = String(code ?? '').trim()
  if (!raw) return ''
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'language' }).of(raw)
    if (name && name.toLowerCase() !== raw.toLowerCase()) return name
  } catch {
    // Unknown or malformed code: fall through to the code itself.
  }
  return raw.toUpperCase()
}

export const SentimentDonut: FC<{
  mentions: number
  // "mentions" for Social Listening, "reviews" for Review Analysis.
  noun?: string
  nounSingular?: string
  sentiment: TopicSentiment
  // "of total" for a topic, "of topic" for a subtopic drill-down.
  shareLabel?: string
  shareOfTotal: number
  // The hover card's heading; "Total Mentions" when unset.
  totalLabel?: string
}> = ({
  mentions,
  noun = 'mentions',
  nounSingular = 'mention',
  sentiment,
  shareLabel = 'of total',
  shareOfTotal,
  totalLabel,
}) => {
  const fractions = sentimentFractions(sentiment)
  const dominant = dominantSentiment(sentiment)
  const dominantPct = Math.round(fractions[dominant] * 100)

  // Drawn as three arcs on one circle. Each starts where the last ended, which
  // is what `offset` accumulates.
  let offset = 0
  const arcs = RING_ORDER.map((key) => {
    const length = fractions[key] * CIRCUMFERENCE
    const arc = { dash: length, key, start: offset }
    offset += length
    return arc
  }).filter((arc) => arc.dash > 0)

  return (
    <div className={styles.donutWrap}>
      <SentimentHoverCard
        className={styles.donutBox}
        sentiment={sentiment}
        total={mentions}
        totalLabel={totalLabel}
      >
        <svg
          aria-hidden="true"
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width={SIZE}
        >
          {/* Rotated so the first arc starts at 12 o'clock. */}
          <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              fill="none"
              r={RADIUS}
              stroke="#E9ECF8"
              strokeWidth={STROKE}
            />
            {arcs.map((arc) => {
              // Only break between arcs: a single full ring has no seam.
              const gap = arcs.length > 1 ? Math.min(ARC_GAP, arc.dash / 2) : 0
              const dash = Math.max(0, arc.dash - gap)
              return (
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  fill="none"
                  key={arc.key}
                  r={RADIUS}
                  stroke={SENTIMENT_COLORS[arc.key]}
                  strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
                  strokeDashoffset={-(arc.start + gap / 2)}
                  strokeWidth={STROKE}
                />
              )
            })}
          </g>
        </svg>
        <span className={styles.donutCenter}>
          <span className={styles.donutPct}>{dominantPct}%</span>
          <span className={styles.donutCount}>{mentions.toLocaleString()}</span>
        </span>
      </SentimentHoverCard>

      <div className={styles.donutMeta}>
        <span
          className={`${styles.donutHeadline} ${
            mentions > 0 ? HEADLINE_CLASS[dominant] : ''
          }`}
        >
          {mentions > 0 ? DOMINANT_LABEL[dominant] : `No ${noun} yet`}
        </span>
        <span className={styles.donutSub}>
          <strong>{`${mentions.toLocaleString()} ${mentions === 1 ? nounSingular : noun}`}</strong>
          {` · ${shareOfTotal}% ${shareLabel}`}
        </span>
        <div className={styles.donutLegend}>
          {LEGEND.map((key) => (
            <span className={styles.legendItem} key={key}>
              <span
                className={styles.legendDot}
                style={{ background: SENTIMENT_COLORS[key] }}
              />
              {LEGEND_LABEL[key]}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

const hasSentiment = (sentiment: TopicSentiment): boolean =>
  (sentiment.positive ?? 0) +
    (sentiment.neutral ?? 0) +
    (sentiment.negative ?? 0) >
  0

export const PlatformBars: FC<{ platforms: PlatformShare[] }> = ({
  platforms,
}) => {
  // Bars are read against the leading platform so the longest one fills the
  // track; against a raw percentage every bar would look stunted.
  const max = platforms.reduce((acc, entry) => Math.max(acc, entry.share), 0)

  if (platforms.length === 0) {
    return <span className={styles.cardEmpty}>No platforms recorded yet</span>
  }

  return (
    <div
      className={`${styles.platformList} ${
        platforms.length < 5 ? styles.platformListSparse : ''
      }`}
    >
      {platforms.map((entry) => (
        <div className={styles.platformRow} key={entry.platform}>
          <span className={styles.platformIcon}>
            {platformIcon(entry.platform)}
          </span>
          <span className={styles.platformName}>
            {platformName(entry.platform)}
          </span>
          {/* Split by sentiment when the read carries it; a plain purple
              fill otherwise (older responses, nothing classified yet). */}
          {entry.sentiment && hasSentiment(entry.sentiment) ? (
            <SentimentHoverCard
              className={styles.platformTrack}
              sentiment={entry.sentiment}
              total={entry.mentions ?? 0}
            >
              <SentimentBar
                className={styles.platformBar}
                fill={max > 0 ? entry.share / max : 0}
                sentiment={entry.sentiment}
              />
            </SentimentHoverCard>
          ) : (
            <span className={styles.platformTrack}>
              <span
                className={styles.platformFill}
                style={{
                  width: `${max > 0 ? (entry.share / max) * 100 : 0}%`,
                }}
              />
            </span>
          )}
          <span className={styles.platformPct}>
            {typeof entry.mentions === 'number' && (
              <span className={styles.platformCount}>
                {entry.mentions.toLocaleString('en-US').replace(/,/g, ' ')}
              </span>
            )}
            {entry.share}%
          </span>
        </div>
      ))}
    </div>
  )
}

// The topic popup's platform card in Content Analysis: the platforms as a
// labelled list beside a donut, the total in its centre. The host supplies
// the palette so the popup matches the rest of its page.
export const PlatformDonut: FC<{
  colorOf: (platform: string, index: number) => string
  // "Posts" / "Comments": the centre label and the per-row unit.
  noun: string
  nounSingular: string
  platforms: { count: number; platform: string }[]
}> = ({ colorOf, noun, nounSingular, platforms }) => {
  const shown = platforms.filter((entry) => entry.count > 0)
  const total = shown.reduce((acc, entry) => acc + entry.count, 0)

  if (shown.length === 0) {
    return <span className={styles.cardEmpty}>No platforms recorded yet</span>
  }

  let offset = 0
  const arcs = shown.map((entry, index) => {
    const length = (entry.count / total) * CIRCUMFERENCE
    const arc = {
      color: colorOf(entry.platform, index),
      dash: length,
      key: entry.platform,
      start: offset,
    }
    offset += length
    return arc
  })

  return (
    <div className={styles.platformDonut}>
      <div className={styles.platformDonutList}>
        {shown.map((entry, index) => (
          <div className={styles.platformDonutRow} key={entry.platform}>
            <span className={styles.platformIcon}>
              {platformIcon(entry.platform)}
            </span>
            <span className={styles.platformDonutName}>
              {platformName(entry.platform)}
              <span className={styles.platformDonutCount}>
                {` - ${entry.count.toLocaleString()} ${
                  entry.count === 1 ? nounSingular : noun
                }`}
              </span>
            </span>
            <span
              className={styles.legendDot}
              style={{ background: colorOf(entry.platform, index) }}
            />
          </div>
        ))}
      </div>
      <div className={styles.donutBox}>
        <svg
          aria-hidden="true"
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          width={SIZE}
        >
          <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
            {arcs.map((arc) => {
              const gap = arcs.length > 1 ? Math.min(ARC_GAP, arc.dash / 2) : 0
              const dash = Math.max(0, arc.dash - gap)
              return (
                <circle
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  fill="none"
                  key={arc.key}
                  r={RADIUS}
                  stroke={arc.color}
                  strokeDasharray={`${dash} ${CIRCUMFERENCE - dash}`}
                  strokeDashoffset={-(arc.start + gap / 2)}
                  strokeWidth={STROKE}
                />
              )
            })}
          </g>
        </svg>
        <span className={styles.donutCenter}>
          <span className={styles.donutCount}>
            {noun.charAt(0).toUpperCase() + noun.slice(1)}
          </span>
          <span className={styles.donutPct}>{total.toLocaleString()}</span>
        </span>
      </div>
    </div>
  )
}

export const SubtopicBar: FC<{
  max: number
  mentions: number
  sentiment: TopicSentiment
  share: number
  totalLabel?: string
}> = ({ mentions, sentiment, share, max, totalLabel }) => {
  const fractions = sentimentFractions(sentiment)
  const filled = max > 0 ? Math.min(1, share / max) : 0
  // Below this the fill is too short to hold the digits.
  const inside = filled >= 0.2

  return (
    <SentimentHoverCard
      className={styles.subBarWrap}
      sentiment={sentiment}
      total={mentions}
      totalLabel={totalLabel}
    >
      <span className={styles.subBarTrack}>
        {LEGEND.map((key) => {
          const width = fractions[key] * filled * 100
          // A zero-width segment would still take a slice of the flex gap and
          // show as a sliver of the wrong colour.
          if (width <= 0) return null
          return (
            <span
              className={styles.subBarSegment}
              key={key}
              style={{ background: SENTIMENT_COLORS[key], width: `${width}%` }}
            />
          )
        })}
        {/* The count rides the end of the fill, as in the design: inside the
          last segment in white when the bar is long enough to hold it, just
          past the fill in dark text when it is not. Pinning it to the track's
          right edge put half the digits on the pale track once a bar ended
          near, but not at, the edge — white on white. */}
        <span
          className={`${styles.subBarCount} ${
            inside ? styles.subBarCountOnFill : ''
          }`}
          style={
            inside
              ? { right: `calc(${(1 - filled) * 100}% + 8px)` }
              : { left: `calc(${filled * 100}% + 8px)` }
          }
        >
          {mentions.toLocaleString()}
        </span>
      </span>
    </SentimentHoverCard>
  )
}

// --- Mentions trend plot ---------------------------------------------------
// Drawn as an SVG in a fixed 700 x 96 user space and scaled by the container,
// so the geometry stays simple while the card stays fluid.
const PLOT_W = 700
const PLOT_H = 96
const PLOT_LEFT = 44
const PLOT_RIGHT = 24
const PLOT_BOTTOM = 18
// A "20 Aug" label needs ~70 user units to stay legible at the card's
// rendered width, so the 632-unit plot fits about eight of them. 7-Day shows
// every day, 14-Day every other, 30-Day every fourth — counted back from the
// newest point so the last date is always the one that is labelled.
const MAX_X_LABELS = 8

const niceCeiling = (max: number): number => {
  if (!Number.isFinite(max) || max <= 0) return 10
  const magnitude = 10 ** Math.floor(Math.log10(max))
  const step = [1, 2, 5, 10].find((s) => s * magnitude >= max) ?? 10
  // An even ceiling keeps the midpoint tick a whole number.
  const ceiling = step * magnitude
  return ceiling % 2 === 0 || ceiling >= 10 ? ceiling : ceiling * 2
}

export const MentionsPlot: FC<{
  emptyLabel?: string
  points: MentionsPoint[]
}> = ({ emptyLabel = 'No mentions in this period', points }) => {
  if (!points.length) {
    return <span className={styles.cardEmpty}>{emptyLabel}</span>
  }

  const max = points.reduce((acc, p) => Math.max(acc, p.value), 0)
  // Round the ceiling up to a clean thousand so the gridlines read 0 / 1 000 /
  // 2 000 rather than an arbitrary maximum.
  // Round the ceiling up to a clean step (1, 2 or 5 × a power of ten) that
  // sits just above the peak, so a 19-mention topic is not drawn flat against
  // a 1 000 axis and a 19 000-mention one still reads in round thousands.
  const ceiling = niceCeiling(max)
  const ticks = [0, ceiling / 2, ceiling]

  const plotWidth = PLOT_W - PLOT_LEFT - PLOT_RIGHT
  const plotHeight = PLOT_H - PLOT_BOTTOM
  const x = (i: number) =>
    PLOT_LEFT +
    (points.length === 1
      ? plotWidth / 2
      : (i / (points.length - 1)) * plotWidth)
  const y = (v: number) => plotHeight - (v / ceiling) * (plotHeight - 6)

  const labelEvery = Math.max(1, Math.ceil(points.length / MAX_X_LABELS))
  const isLabelled = (i: number) => (points.length - 1 - i) % labelEvery === 0

  const line = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)} ${y(p.value)}`)
    .join(' ')
  const area = `${line} L${x(points.length - 1)} ${plotHeight} L${x(0)} ${plotHeight} Z`

  return (
    <svg preserveAspectRatio="none" viewBox={`0 0 ${PLOT_W} ${PLOT_H}`}>
      <defs>
        <linearGradient id="mentionsFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#8080FF" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#8080FF" stopOpacity="0" />
        </linearGradient>
      </defs>

      {ticks.map((tick) => (
        <g key={tick}>
          <line
            className={styles.plotGrid}
            x1={PLOT_LEFT}
            x2={PLOT_W - PLOT_RIGHT}
            y1={y(tick)}
            y2={y(tick)}
          />
          <text
            className={styles.plotAxisText}
            textAnchor="end"
            x={PLOT_LEFT - 8}
            y={y(tick) + 4}
          >
            {tick.toLocaleString('en-US').replace(/,/g, ' ')}
          </text>
        </g>
      ))}

      <path d={area} fill="url(#mentionsFill)" />
      <path
        d={line}
        fill="none"
        stroke="#8080FF"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
      />
      {points.map((p, i) => (
        <circle
          cx={x(i)}
          cy={y(p.value)}
          fill="#FFFFFF"
          key={`${i}-${p.label}`}
          r="3.5"
          stroke="#8080FF"
          strokeWidth="1.5"
        />
      ))}
      {points.map((p, i) =>
        isLabelled(i) ? (
          <text
            className={styles.plotAxisText}
            key={`label-${i}-${p.label}`}
            textAnchor="middle"
            x={x(i)}
            y={PLOT_H - 2}
          >
            {p.label}
          </text>
        ) : null
      )}
    </svg>
  )
}

/**
 * "1m ago" from an absolute timestamp.
 *
 * The API returns the instant rather than a pre-rendered label so the text
 * stays correct while a modal is left open, and so the same payload can be
 * cached without going stale.
 */
export const formatTimeAgo = (iso?: string | null): string => {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (!Number.isFinite(then)) return ''
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.round(days / 30)
  return months < 12 ? `${months}mo ago` : `${Math.round(months / 12)}y ago`
}
